import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { Button, Card, Callout, Spinner, Badge, CopyField, TextField, Modal } from "@/design/components";
import { ApiError } from "@/api/client";
import { useAuth } from "@/app/auth";
import { getOrgProfile, rotateOrgId, exportOrg, eraseOrg, renameOrg, acceptTerms, type OrgProfile } from "@/api/org";
import {
  getEnabledProviders,
  getLinkedProviders,
  startProviderLink,
  unlinkProvider,
  PROVIDER_LABEL,
  type SocialProvider,
} from "@/api/oauth";
import { TIER_LABEL } from "@/features/onboarding/tiers";
import { LEGAL_TERMS_URL } from "@/config";
import { getMe, changePassword, setPassword, type TierId } from "@/api/auth";
import styles from "./SettingsScreen.module.css";

const apiMessage = (err: unknown, fallback: string): string =>
  err instanceof ApiError ? err.message : fallback;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** Turn the GDPR export JSON into a downloaded file (Blob + object URL + synthetic
 *  anchor). Always revoke the URL — an un-revoked object URL leaks until unload. */
function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Read the provider-link outcome the OAuth callback left in the URL fragment. */
function readLinkNotice(): { tone: "success" | "danger" | "info"; text: string } | null {
  try {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const linked = params.get("linked");
    if (linked) {
      const label = PROVIDER_LABEL[linked as SocialProvider] ?? linked;
      return { tone: "success", text: `${label} is now linked — you can use it to sign in.` };
    }
    // The link flow's already-linked short-circuit (the server bounced us straight back).
    const already = params.get("already");
    if (already) {
      const label = PROVIDER_LABEL[already as SocialProvider] ?? already;
      return { tone: "info", text: `${label} is already linked to your account.` };
    }
    const err = params.get("linkerror");
    if (err) {
      const text =
        err === "in_use"
          ? "That account is already linked to a different Cogent user."
          : err === "expired"
            ? "That linking request expired. Please try connecting again."
            : "We couldn't link that account. Please try again.";
      return { tone: "danger", text };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Settings (Phase 7 / FR27, FR11) — org profile + the compliance surface: T&C
 * acceptance, Org_ID rotation (one-time reveal), GDPR data export (download), and
 * the owner-only org erasure (typed-name confirmation). The JWT carries no role, so
 * owner-gated actions are enforced by the API and the UI surfaces a 403 gracefully
 * rather than guessing who the owner is.
 */
export function SettingsScreen() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const qc = useQueryClient();
  const orgQuery = useQuery({ queryKey: ["org"], queryFn: ({ signal }) => getOrgProfile(signal) });

  // A provider-link round-trip returns here with the outcome in the URL fragment
  // (#linked=<provider> / #linkerror=<reason>). Read it once for a banner, then scrub
  // the fragment from history so a reload doesn't re-show it.
  const [linkNotice] = useState(readLinkNotice);
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, []);

  // Org rename (owner-only server-side → a non-owner admin gets a 403 surfaced in the
  // modal) + T&C re-accept. Both return the refreshed profile, so we seed the cache.
  const [renameOpen, setRenameOpen] = useState(false);
  const rename = useMutation({
    mutationFn: (name: string) => renameOrg(name),
    onSuccess: (updated: OrgProfile) => {
      qc.setQueryData(["org"], updated);
      setRenameOpen(false);
    },
  });
  const accept = useMutation({
    mutationFn: () => acceptTerms(),
    onSuccess: (updated: OrgProfile) => qc.setQueryData(["org"], updated),
  });
  function openRename() {
    rename.reset();
    setRenameOpen(true);
  }
  function closeRename() {
    if (rename.isPending) return;
    setRenameOpen(false);
  }

  // Org_ID rotation → reveal the new value exactly once (mirrors the welcome screen).
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const rotate = useMutation({
    mutationFn: () => rotateOrgId(),
    onSuccess: (res) => {
      setRotateConfirmOpen(false);
      setRevealed(res.orgId);
    },
  });

  // GDPR export → download the returned JSON.
  const exportMut = useMutation({
    mutationFn: () => exportOrg(),
    onSuccess: (data) => downloadJson(data, `cogent-org-export-${data.exportedAt.slice(0, 10)}.json`),
  });

  // GDPR erase → owner-only, typed org name. On success the org is gone → sign out.
  const [eraseOpen, setEraseOpen] = useState(false);
  const erase = useMutation({
    mutationFn: (confirmation: string) => eraseOrg(confirmation),
    onSuccess: () => {
      signOut();
      navigate("/login", { replace: true });
    },
  });

  function openRotate() {
    rotate.reset();
    setRotateConfirmOpen(true);
  }
  function closeRotate() {
    if (rotate.isPending) return; // a rotation is in flight — don't drop the reveal
    setRotateConfirmOpen(false);
  }
  function closeReveal() {
    setRevealed(null); // the new Org_ID is gone from memory once dismissed
  }
  function openErase() {
    erase.reset();
    setEraseOpen(true);
  }
  function closeErase() {
    if (erase.isPending) return;
    setEraseOpen(false);
  }

  const profile = orgQuery.data;
  const org = profile?.org;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="u-eyebrow">Control plane</p>
          <h1 className={styles.h1}>Settings</h1>
        </div>
      </header>

      {/* Callout sets role=status (success) / role=alert (danger) itself. */}
      {linkNotice && <Callout tone={linkNotice.tone}>{linkNotice.text}</Callout>}

      {orgQuery.isPending && (
        <div className={styles.loading}>
          <Spinner size={22} color="var(--accent)" /> <span>Loading settings…</span>
        </div>
      )}
      {orgQuery.isError && (
        <Callout tone="danger" title="Couldn't load your settings">
          {apiMessage(orgQuery.error, "Please try again shortly.")}
        </Callout>
      )}

      {profile && org && (
        <>
          {/* Organization */}
          <Card className={styles.card}>
            <h2 className={styles.cardTitle}>Organization</h2>
            <dl className={styles.facts}>
              <div className={styles.fact}>
                <dt>Name</dt>
                <dd>{org.name}</dd>
              </div>
              <div className={styles.fact}>
                <dt>Plan</dt>
                <dd>
                  <Badge tone="accent">{TIER_LABEL[org.tier as TierId] ?? org.tier}</Badge>
                </dd>
              </div>
              <div className={styles.fact}>
                <dt>Status</dt>
                <dd>
                  <Badge tone={org.status === "active" ? "success" : "neutral"}>{org.status}</Badge>
                </dd>
              </div>
              <div className={styles.fact}>
                <dt>Created</dt>
                <dd>{formatDate(org.createdAt)}</dd>
              </div>
            </dl>
            {/* Rename is owner-only server-side; a non-owner admin sees a 403 in the modal. */}
            <Button variant="secondary" onClick={openRename}>
              Rename organization
            </Button>
          </Card>

          {/* Terms & conditions */}
          <Card className={styles.card}>
            <h2 className={styles.cardTitle}>Terms &amp; conditions</h2>
            <dl className={styles.facts}>
              <div className={styles.fact}>
                <dt>You accepted</dt>
                <dd>
                  {profile.terms.accepted
                    ? `${profile.terms.accepted}${profile.terms.acceptedAt ? ` · ${formatDate(profile.terms.acceptedAt)}` : ""}`
                    : "Not recorded"}
                </dd>
              </div>
              <div className={styles.fact}>
                <dt>Current version</dt>
                <dd>{profile.terms.current}</dd>
              </div>
            </dl>
            {profile.terms.accepted && profile.terms.accepted !== profile.terms.current && (
              <Callout tone="info">
                A newer version ({profile.terms.current}) is now in effect. Please review the current terms.
              </Callout>
            )}
            <a className={styles.link} href={LEGAL_TERMS_URL} target="_blank" rel="noreferrer">
              Read the current terms
            </a>
            {/* Re-accept when the caller hasn't accepted the version in force. */}
            {profile.terms.accepted !== profile.terms.current && (
              <div className={styles.termsAction}>
                <Button onClick={() => accept.mutate()} loading={accept.isPending}>
                  Accept current terms
                </Button>
                {accept.isError && (
                  <Callout tone="danger">{apiMessage(accept.error, "Couldn't record your acceptance.")}</Callout>
                )}
              </div>
            )}
          </Card>

          {/* Org_ID */}
          <Card className={styles.card}>
            <h2 className={styles.cardTitle}>Org_ID</h2>
            <p className={styles.blurb}>
              Your Org_ID is the key agents use to join your channels. For your security we store it
              only as a <strong>one-way hash</strong>, never in plaintext — it's shown only once, at
              creation and after each rotation.
            </p>
            <Callout tone="info" title="Lost your Org_ID? We can't recover it — only rotate it.">
              Because we keep only a one-way hash of your Org_ID (and don't retain your channel
              messages in any content store), no one — including us — can recover it or read a channel
              without it. If it's lost or leaked, rotate it below: a new Org_ID is issued and your
              channels are re-scoped. Connected agents are dropped and must update.
            </Callout>
            <Button variant="secondary" onClick={openRotate}>
              Rotate Org_ID
            </Button>
          </Card>

          {/* Password (COG-12) — Change form for an email/password account; the social
              info message + Set form for a social account with no user-known password. */}
          <PasswordCard />

          {/* Linked sign-in accounts (Phase 7) — renders nothing when social login is off. */}
          <LinkedAccountsCard />

          {/* Your data (GDPR export) */}
          <Card className={styles.card}>
            <h2 className={styles.cardTitle}>Your data</h2>
            <p className={styles.blurb}>
              Download a copy of your organization's data — your profile, team, channels, and audit
              trail — as JSON (GDPR data access). Secrets are never included.
            </p>
            <Button onClick={() => exportMut.mutate()} loading={exportMut.isPending}>
              Download my data
            </Button>
            {exportMut.isError && (
              <Callout tone="danger">{apiMessage(exportMut.error, "Couldn't prepare your export.")}</Callout>
            )}
          </Card>

          {/* Danger zone (GDPR erase) */}
          <Card className={`${styles.card} ${styles.danger}`}>
            <h2 className={styles.cardTitle}>Danger zone</h2>
            <p className={styles.blurb}>
              Permanently erase <strong>{org.name}</strong> and everything in it — team, channels,
              audit trail, and Slack mappings. This is immediate and cannot be undone. Only the
              organization owner can do this.
            </p>
            <Button variant="danger" onClick={openErase}>
              Delete organization…
            </Button>
          </Card>
        </>
      )}

      {/* Rotate — confirm */}
      <Modal
        open={rotateConfirmOpen}
        onClose={closeRotate}
        title="Rotate your Org_ID?"
        footer={
          <>
            <Button variant="ghost" onClick={closeRotate} disabled={rotate.isPending}>
              Cancel
            </Button>
            <Button variant="danger" loading={rotate.isPending} onClick={() => rotate.mutate()}>
              Rotate Org_ID
            </Button>
          </>
        }
      >
        <p>
          A new Org_ID is generated and the current one stops working <strong>immediately</strong>.
          Any agents using the old Org_ID are disconnected until you update them. The new value is
          shown only once.
        </p>
        {rotate.isError && (
          <Callout tone="danger">{apiMessage(rotate.error, "Couldn't rotate the Org_ID.")}</Callout>
        )}
      </Modal>

      {/* Rotate — one-time reveal */}
      <Modal
        open={revealed !== null}
        onClose={closeReveal}
        title="Your new Org_ID"
        footer={<Button onClick={closeReveal}>Done</Button>}
      >
        {revealed && (
          <div className={styles.reveal}>
            <p>Save this now — like the first time, it's shown only once.</p>
            <CopyField label="Org_ID" value={revealed} multiline />
            <Callout tone="warning" title="The old Org_ID no longer works">
              Update every agent that joins your channels with this new Org_ID.
            </Callout>
          </div>
        )}
      </Modal>

      {/* Rename */}
      <Modal open={renameOpen} onClose={closeRename} title="Rename organization">
        {org && (
          <RenameForm
            currentName={org.name}
            pending={rename.isPending}
            error={rename.isError ? apiMessage(rename.error, "Couldn't rename the organization.") : null}
            onSubmit={(name) => rename.mutate(name)}
          />
        )}
      </Modal>

      {/* Erase — typed confirmation */}
      <Modal
        open={eraseOpen}
        onClose={closeErase}
        title="Delete this organization?"
        footer={
          <Button variant="ghost" onClick={closeErase} disabled={erase.isPending}>
            Cancel
          </Button>
        }
      >
        <p>
          This permanently erases <strong>{org?.name}</strong> and all of its data. It cannot be
          undone. To confirm, type the organization's name exactly.
        </p>
        {org && (
          <EraseForm
            orgName={org.name}
            pending={erase.isPending}
            error={erase.isError ? apiMessage(erase.error, "Couldn't erase the organization.") : null}
            onConfirm={(confirmation) => erase.mutate(confirmation)}
          />
        )}
      </Modal>
    </div>
  );
}

/**
 * Linked sign-in accounts (Phase 7). Lists every CONFIGURED provider with either its
 * linked email + an Unlink action, or a Connect action that begins the OAuth round-trip
 * (a full-page navigation to the API-minted start URL). Renders nothing at all when the
 * API has no providers configured, so it's inert until social login is switched on.
 */
function LinkedAccountsCard() {
  const qc = useQueryClient();
  const enabledQuery = useQuery({ queryKey: ["oauth-enabled"], queryFn: ({ signal }) => getEnabledProviders(signal) });
  const linkedQuery = useQuery({ queryKey: ["oauth-identities"], queryFn: ({ signal }) => getLinkedProviders(signal) });
  // Which provider the user is currently acting on (drives the per-row spinner).
  const [busy, setBusy] = useState<SocialProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A benign, non-error in-page note (e.g. the provider was already linked).
  const [notice, setNotice] = useState<string | null>(null);

  const linkMut = useMutation({
    mutationFn: (p: SocialProvider) => startProviderLink(p),
    onMutate: (p) => {
      setBusy(p);
      setError(null);
      setNotice(null);
    },
    onSuccess: (res, p) => {
      // Navigate ONLY when the server handed back a start URL; when it reports the provider is
      // already linked, don't bounce through consent — refresh the list and show a benign note.
      if (res.startUrl) {
        window.location.assign(res.startUrl); // hand off to the OAuth flow
        return;
      }
      setBusy(null);
      setNotice(`${PROVIDER_LABEL[p]} is already linked to your account.`);
      void qc.invalidateQueries({ queryKey: ["oauth-identities"] });
    },
    onError: (err) => {
      setBusy(null);
      setError(apiMessage(err, "Couldn't start linking. Please try again."));
    },
  });
  const unlinkMut = useMutation({
    mutationFn: (p: SocialProvider) => unlinkProvider(p),
    onMutate: (p) => {
      setBusy(p);
      setError(null);
      setNotice(null);
    },
    onSuccess: () => {
      setBusy(null);
      void qc.invalidateQueries({ queryKey: ["oauth-identities"] });
    },
    onError: (err) => {
      setBusy(null);
      setError(apiMessage(err, "Couldn't unlink that account. Please try again."));
    },
  });

  const enabled = enabledQuery.data ?? [];
  // No providers configured (or the probe is settled-empty) → render nothing.
  if (enabledQuery.isSuccess && enabled.length === 0) return null;
  if (enabled.length === 0) return null; // still loading / probe failed → defer to the next render

  const linked = new Map((linkedQuery.data ?? []).map((l) => [l.provider, l]));

  return (
    <Card className={styles.card}>
      <h2 className={styles.cardTitle}>Linked accounts</h2>
      <p className={styles.blurb}>
        Connect a sign-in provider to sign in with one click. We only use it to confirm it's you — we
        never post anything on your behalf.
      </p>
      {/* Callout(danger) already carries role=alert. */}
      {error && <Callout tone="danger">{error}</Callout>}
      {notice && <Callout tone="info">{notice}</Callout>}
      <ul className={styles.linkedList}>
        {enabled.map((p) => {
          const l = linked.get(p);
          return (
            <li key={p} className={styles.linkedRow}>
              <div className={styles.linkedMeta}>
                <span className={styles.linkedName}>{PROVIDER_LABEL[p]}</span>
                {l ? (
                  <span className={styles.linkedEmail}>{l.email ?? "Connected"}</span>
                ) : (
                  <span className={styles.linkedMuted}>Not connected</span>
                )}
              </div>
              {l ? (
                <Button variant="ghost" onClick={() => unlinkMut.mutate(p)} loading={busy === p}>
                  Unlink
                </Button>
              ) : (
                <Button variant="secondary" onClick={() => linkMut.mutate(p)} loading={busy === p}>
                  Connect
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/**
 * Password (COG-12). Branches on the authoritative `hasPassword` from GET /auth/me:
 *  - true  → an email/password account → the Change-password form (Current/New/Confirm).
 *  - false → a social account with no user-known password → the info message + the
 *            Set-password form (New/Confirm). After a successful set the me query is
 *            invalidated so the card flips to change-mode.
 * The me query is local to this card (independent of the cached session identity, which
 * doesn't carry hasPassword) — a load error / pending state simply renders nothing.
 */
function PasswordCard() {
  const qc = useQueryClient();
  const meQuery = useQuery({ queryKey: ["me"], queryFn: ({ signal }) => getMe(signal) });

  const change = useMutation({
    mutationFn: (v: { currentPassword: string; newPassword: string }) => changePassword(v),
  });
  const set = useMutation({
    mutationFn: (v: { newPassword: string }) => setPassword(v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });

  // Until the identity resolves (or if it errored), render nothing rather than guess.
  if (!meQuery.data) return null;

  return (
    <Card className={styles.card}>
      <h2 className={styles.cardTitle}>Password</h2>
      {meQuery.data.hasPassword ? (
        <ChangePasswordForm
          pending={change.isPending}
          success={change.isSuccess}
          error={change.isError ? apiMessage(change.error, "Couldn't change your password.") : null}
          onSubmit={(currentPassword, newPassword) => {
            change.reset();
            change.mutate({ currentPassword, newPassword });
          }}
        />
      ) : (
        <>
          <Callout tone="info">
            You signed up via social login and don't have a password set. Set one to enable
            email/password login.
          </Callout>
          <SetPasswordForm
            pending={set.isPending}
            error={set.isError ? apiMessage(set.error, "Couldn't set your password.") : null}
            onSubmit={(newPassword) => {
              set.reset();
              set.mutate({ newPassword });
            }}
          />
        </>
      )}
    </Card>
  );
}

/** Min-8 + confirm-match schema shared by both password forms. */
const passwordRules = z
  .object({ newPassword: z.string().min(8, "Password must be at least 8 characters"), confirm: z.string() })
  .refine((v) => v.newPassword === v.confirm, { message: "Passwords don't match", path: ["confirm"] });

/**
 * Change-password form (email/password account). Uncontrolled (RHF) for the same
 * focus-stability reason as the other forms. The API re-verifies the current password
 * and min length; a wrong current password surfaces as a 400 in `error`.
 */
function ChangePasswordForm({
  pending,
  success,
  error,
  onSubmit,
}: {
  pending: boolean;
  success: boolean;
  error: string | null;
  onSubmit: (currentPassword: string, newPassword: string) => void;
}) {
  const schema = useMemo(
    () =>
      z
        .object({
          currentPassword: z.string().min(1, "Enter your current password"),
          newPassword: z.string().min(8, "Password must be at least 8 characters"),
          confirm: z.string(),
        })
        .refine((v) => v.newPassword === v.confirm, { message: "Passwords don't match", path: ["confirm"] }),
    [],
  );
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ currentPassword: string; newPassword: string; confirm: string }>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: "", newPassword: "", confirm: "" },
  });

  return (
    <form
      className={styles.renameForm}
      onSubmit={handleSubmit((v) => {
        onSubmit(v.currentPassword, v.newPassword);
        reset();
      })}
      noValidate
    >
      <TextField
        label="Current password"
        type="password"
        autoComplete="current-password"
        error={errors.currentPassword?.message}
        {...register("currentPassword")}
      />
      <TextField
        label="New password"
        type="password"
        autoComplete="new-password"
        error={errors.newPassword?.message}
        {...register("newPassword")}
      />
      <TextField
        label="Confirm new password"
        type="password"
        autoComplete="new-password"
        error={errors.confirm?.message}
        {...register("confirm")}
      />
      {error && (
        <div role="alert">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}
      {success && <Callout tone="success">Password changed.</Callout>}
      <Button type="submit" fullWidth loading={pending}>
        Change Password
      </Button>
    </form>
  );
}

/**
 * Set-password form (social account with no user-known password). On success the parent
 * invalidates the me query, so this form is replaced by the Change form.
 */
function SetPasswordForm({
  pending,
  error,
  onSubmit,
}: {
  pending: boolean;
  error: string | null;
  onSubmit: (newPassword: string) => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ newPassword: string; confirm: string }>({
    resolver: zodResolver(passwordRules),
    defaultValues: { newPassword: "", confirm: "" },
  });

  return (
    <form className={styles.renameForm} onSubmit={handleSubmit((v) => onSubmit(v.newPassword))} noValidate>
      <TextField
        label="New password"
        type="password"
        autoComplete="new-password"
        error={errors.newPassword?.message}
        {...register("newPassword")}
      />
      <TextField
        label="Confirm new password"
        type="password"
        autoComplete="new-password"
        error={errors.confirm?.message}
        {...register("confirm")}
      />
      {error && (
        <div role="alert">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}
      <Button type="submit" fullWidth loading={pending}>
        Set Password
      </Button>
    </form>
  );
}

/**
 * Org rename form. Uncontrolled (RHF) for the same focus-stability reason as
 * EraseForm. Pre-fills the current name; requires a non-empty value (the API
 * re-validates + enforces OWNER-only).
 */
function RenameForm({
  currentName,
  pending,
  error,
  onSubmit,
}: {
  currentName: string;
  pending: boolean;
  error: string | null;
  onSubmit: (name: string) => void;
}) {
  const schema = useMemo(() => z.object({ name: z.string().trim().min(1, "Enter an organization name") }), []);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ name: string }>({ resolver: zodResolver(schema), defaultValues: { name: currentName } });

  return (
    <form className={styles.renameForm} onSubmit={handleSubmit((v) => onSubmit(v.name))} noValidate>
      <TextField label="Organization name" autoComplete="off" error={errors.name?.message} {...register("name")} />
      {error && (
        <div role="alert">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}
      <Button type="submit" fullWidth loading={pending}>
        Save name
      </Button>
    </form>
  );
}

/**
 * The erase confirmation form. Uncontrolled (React Hook Form) ON PURPOSE: a
 * controlled input would re-render the parent on each keystroke, which re-runs the
 * Modal's focus effect and steals focus mid-type. The exact-name match is validated
 * here (Zod) AND server-side (the API rejects a mismatch before any deletion).
 */
function EraseForm({
  orgName,
  pending,
  error,
  onConfirm,
}: {
  orgName: string;
  pending: boolean;
  error: string | null;
  onConfirm: (confirmation: string) => void;
}) {
  const schema = useMemo(
    () =>
      z.object({
        confirmation: z.string().refine((v) => v === orgName, "Type the organization name exactly to confirm"),
      }),
    [orgName],
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ confirmation: string }>({ resolver: zodResolver(schema), defaultValues: { confirmation: "" } });

  return (
    <form className={styles.eraseForm} onSubmit={handleSubmit((v) => onConfirm(v.confirmation))} noValidate>
      <TextField
        label={`Type "${orgName}" to confirm`}
        autoComplete="off"
        placeholder={orgName}
        error={errors.confirmation?.message}
        {...register("confirmation")}
      />
      {error && (
        <div role="alert">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}
      <Button type="submit" variant="danger" fullWidth loading={pending}>
        Delete organization
      </Button>
    </form>
  );
}
